import React, { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { T } from '../lib/theme'
import { ROLE_LABELS } from '../lib/roles'

const NAV = [
  { to: '/',                icon: '▦',  label: 'Dashboard'        },
  { to: '/agents',          icon: '◉',  label: 'Agents'           },
  { to: '/scoring',         icon: '◎',  label: 'Scoring'          },
  { to: '/target-board',    icon: '⊞',  label: 'Target Board'     },
  { to: '/infractions',     icon: '△',  label: 'Infractions'      },
  { to: '/compliance',      icon: '⚖',  label: 'Compliance'       },
  { to: '/enforcement',     icon: '⚠',  label: 'Exception Mgmt'      },
  { to: '/reports',         icon: '≡',  label: 'Reports'          },
  { to: '/target-settings', icon: '◈',  label: 'Target Settings'  },
  { to: '/settings',        icon: '⊙',  label: 'Settings'         },
]

const ROLE_BADGE_COLOR = {
  admin:              { c:'#58e6a0', bg:'rgba(88,230,160,.15)',  border:'rgba(88,230,160,.3)'  },
  enforcement:        { c:'#f0883e', bg:'rgba(240,136,62,.15)',  border:'rgba(240,136,62,.3)'  },
  compliance_officer: { c:'#1E90FF', bg:'rgba(30,144,255,.15)',  border:'rgba(30,144,255,.3)'  },
  acsm_officer:       { c:'#a371f7', bg:'rgba(163,113,247,.15)', border:'rgba(163,113,247,.3)' },
  guest:              { c:'#8b949e', bg:'rgba(139,148,158,.1)',  border:'rgba(139,148,158,.2)' },
}

export default function Layout({ session, setSession, roleCtx }) {
  const navigate   = useNavigate()
  const [signing,    setSigning]    = useState(false)
  const [collapsed,  setCollapsed]  = useState(false)
  const [tooltip,    setTooltip]    = useState(null)   // { label, top }

  const role = roleCtx?.role || 'guest'
  const rb   = ROLE_BADGE_COLOR[role] || ROLE_BADGE_COLOR.guest

  async function signOut() {
    setSigning(true)
    await supabase.auth.signOut()
    setSession(null)
    navigate('/login')
  }

  const sideW = collapsed ? 60 : 228

  return (
    <div style={{ display:'flex', height:'100vh', fontFamily:T.font, background:T.bg, overflow:'hidden' }}>

      {/* ── Sidebar ── */}
      <aside style={{
        width: sideW, minWidth: sideW,
        background: T.surface, borderRight:`1px solid ${T.border}`,
        display:'flex', flexDirection:'column', flexShrink:0,
        overflowY:'auto', overflowX:'hidden',
        transition:'width .2s ease',
        position:'relative', zIndex:100,
      }}>

        {/* Logo / header */}
        <div style={{ padding: collapsed ? '20px 0' : '20px 16px 16px',
          borderBottom:`1px solid ${T.border}`,
          display:'flex', alignItems:'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          flexShrink:0,
        }}>
          {!collapsed && (
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <img src="/acsm-logo.png" alt="ACSM" style={{ height:32, width:'auto', objectFit:'contain', flexShrink:0 }} />
                <div>
                  <div style={{ color:T.txtPrimary, fontWeight:800, fontSize:12 }}>KTIRS · ACSM</div>
                  <div style={{ color:T.txtMuted, fontSize:9, letterSpacing:'.05em', textTransform:'uppercase' }}>
                    Katsina Informal Sector
                  </div>
                </div>
              </div>
            </div>
          )}
          {collapsed && (
            <img src="/acsm-logo.png" alt="ACSM" style={{ height:30, width:30, objectFit:'contain' }} />
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex:1, padding: collapsed ? '12px 6px' : '12px 10px', position:'relative' }}>
          {!collapsed && (
            <div style={{ fontSize:9, fontWeight:700, color:T.txtMuted, letterSpacing:'.1em',
              textTransform:'uppercase', padding:'0 8px', marginBottom:8 }}>Main Menu</div>
          )}
          {NAV.map(({ to, icon, label }) => (
            <div key={to} style={{ position:'relative' }}
              onMouseEnter={e => collapsed && setTooltip({ label, top: e.currentTarget.getBoundingClientRect().top })}
              onMouseLeave={() => setTooltip(null)}>
              <NavLink to={to} end={to==='/'} style={({ isActive }) => ({
                display:'flex', alignItems:'center',
                gap: collapsed ? 0 : 10,
                padding: collapsed ? '10px 0' : '9px 12px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                marginBottom:2,
                color: isActive ? T.txtPrimary : T.txtSecondary,
                textDecoration:'none', fontSize:12, fontWeight: isActive ? 700 : 500,
                borderRadius:8, background: isActive ? T.blueGlow : 'transparent',
                border: isActive ? `1px solid rgba(30,144,255,.2)` : '1px solid transparent',
                transition:'all .15s',
              })}>
                <span style={{ fontSize:collapsed?18:15, width:collapsed?'100%':20,
                  textAlign:'center', lineHeight:1 }}>{icon}</span>
                {!collapsed && <span>{label}</span>}
              </NavLink>
            </div>
          ))}

          {/* Floating tooltip when collapsed */}
          {collapsed && tooltip && (
            <div style={{
              position:'fixed', left:68, top:tooltip.top+4,
              background:T.surface2, color:T.txtPrimary,
              border:`1px solid ${T.border}`,
              borderRadius:6, padding:'5px 10px',
              fontSize:12, fontWeight:600,
              pointerEvents:'none', zIndex:9999,
              whiteSpace:'nowrap', boxShadow:'0 4px 16px rgba(0,0,0,.5)',
            }}>
              {tooltip.label}
            </div>
          )}
        </nav>

        {/* Footer */}
        <div style={{ padding: collapsed ? '12px 6px' : '14px 16px',
          borderTop:`1px solid ${T.border}`, flexShrink:0 }}>
          {!collapsed && (
            <>
              <div style={{ fontSize:10, color:T.txtMuted, marginBottom:4,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {session?.user?.user_metadata?.full_name || session?.user?.email}
              </div>
              <div style={{ display:'inline-block', padding:'2px 8px', borderRadius:20,
                fontSize:9, fontWeight:700, letterSpacing:'.04em',
                background:rb.bg, color:rb.c, border:`1px solid ${rb.border}`,
                marginBottom:10 }}>
                {ROLE_LABELS[role] || role}
              </div>
              <button onClick={signOut} disabled={signing} style={{ width:'100%', padding:'7px 10px',
                background:'rgba(248,81,73,.08)', color:'#f85149',
                border:'1px solid rgba(248,81,73,.2)', borderRadius:6, fontSize:11, fontWeight:600,
                cursor:'pointer', fontFamily:T.font }}>
                {signing ? '…' : 'Sign Out'}
              </button>
            </>
          )}
          {collapsed && (
            <button onClick={signOut} disabled={signing}
              title="Sign Out"
              style={{ width:'100%', padding:'8px 0', background:'rgba(248,81,73,.08)', color:'#f85149',
                border:'1px solid rgba(248,81,73,.2)', borderRadius:6, fontSize:14,
                cursor:'pointer', textAlign:'center' }}>⏻</button>
          )}
        </div>
      </aside>

      {/* ── Main content ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>

        {/* Top bar */}
        <div style={{ background:T.surface, borderBottom:`1px solid ${T.border}`,
          padding:'10px 20px', display:'flex', alignItems:'center',
          justifyContent:'space-between', flexShrink:0, gap:12 }}>

          {/* Collapse toggle */}
          <button onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{ background:'transparent', border:`1px solid ${T.border}`,
              borderRadius:6, color:T.txtSecondary, padding:'5px 9px',
              fontSize:13, cursor:'pointer', fontFamily:T.font,
              display:'flex', alignItems:'center', justifyContent:'center',
              lineHeight:1, flexShrink:0 }}>
            {collapsed ? '▶' : '◀'}
          </button>

          <div style={{ fontSize:13, fontWeight:700, color:T.txtPrimary, flex:1,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            Agent Compliance Scoring System
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          </div>
        </div>

        {/* Page content */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px', background:T.bg }}>
          <Outlet />
        </div>
      </div>
    </div>
  )
}
